import { useQueries } from "@tanstack/react-query";
import { parseBackend } from "src/backend";
import { usePatents } from "src/patents/PatentsProvider";
import { RenewalOverviewContext, RenewalsContext } from "../interfaces/Renwals";
import { PagedResponse, PRInstruction, PRRenewal, toRenewal, useInstruction, useIpRight, useIpRights, useRenewals } from "./patent_renewals";
import { RenewalState } from "../interfaces/renewal_types";
import { renewalNoticesStatus } from "../state/modern_states";
import { importStatusExtraction } from "./modern_states";
import _ from "lodash";
import { diffDays } from "src/utils/dates";
// import { IpMetaContextProvider } from "./IpMeta";

function findClosestInstruction(renewal: PRRenewal, instructions?: PRInstruction[]) {
  if (!instructions) return undefined

  let result = undefined
  let delta_t = undefined
  instructions.forEach(i => {
    const dt = diffDays(i.created_at, renewal.renewal_date)
    if (dt > 0 && (delta_t === undefined || dt < delta_t)) {
      delta_t = dt
      result = i
    }
  })
  return result
}

export function PRRenewalsProvider({ start, end, states, children }: {start?: string; end?: string; states: RenewalState[], children: React.ReactNode}) {

  const {ipRightById} = useIpRights()
  const {renewals: _prRenewals, isLoading} = useRenewals({start, end, states})


  const prRenewals = _prRenewals.filter(r => r.ip_id in ipRightById)
  // console.log({_prRenewals, prRenewals, ipRightById})

  const ipIds = _.keys(ipRightById)

  // @ts-ignore
  const {data: instructionsByIpId}  = useQueries({
    queries: ipIds.map(ip_id => ({
      queryKey: ['pr', 'instructions', ip_id],
      queryFn: () => parseBackend(fetch(`/api/patent-renewal/pass-through/api/v1/ips/${ip_id}/instructions`))
      // TODO: remove after mid may 2026
        .then((instrs: (PRInstruction[] | PagedResponse<PRInstruction>)) => ({[ip_id]: (instrs && 'items' in instrs) ? instrs.items : instrs})),
      enabled: !!ip_id,
    })),
    combine: (results) => results.reduce((prev, current) => ({...prev, ...current}), {})
  })

  // console.log({instructionsByIpId})

  const renewals = prRenewals
    .map(renewal => toRenewal(renewal, ipRightById[renewal.ip_id], findClosestInstruction(renewal, instructionsByIpId?.[renewal.ip_id])))
    .filter(r => r.feeDueDate)

  //console.log("ALL RENEWALS")
  //console.log({renewals, prRenewals, ipRights})
  const iprByRenewalId = _(prRenewals).map(r => ([r.id, r.ip_id])).fromPairs().value()

  const {instruct, bulkInstruct} = useInstruction({iprByRenewalId})

  const value = {
    renewals,
    instruct,
    bulkInstruct,
    isLoading: isLoading,
  }
  // console.log({renewals})

  return (
    <RenewalsContext.Provider {...{ value }}>{children}</RenewalsContext.Provider>
  )
}


/*
TODO: Functions needed
- Search for IP right by family member ID
- Search for IP right by family member reference
- List all the open Renewals, no matter the IP right
- Best way to (bulk) instruct?
- What are the minimum fields needed to create/import an IP right?
*/
export function PRRenewalOverviewProvider({ familyMemberId, children }: { familyMemberId: number; children: React.ReactNode }) {

  const { memberById } = usePatents()
  const member = memberById[familyMemberId]

  const {ipRight, prIpRight, renewals, isLoading} = useIpRight(member)
  //console.log({renewals})

  const {instruct} = useInstruction({ip_id: prIpRight?.id})

  if (!member) return null
  // TODO: Add more/other checks for PR specific (e.g. grant number is required)
  const ipRightImportStatus = importStatusExtraction({ member, ipRight, prIpRight, validationErrors: [] })
  const renewalsStatus = renewalNoticesStatus({renewalStatus: ipRightImportStatus.status, renewals})

  // console.log({ipRightImportStatus, renewalsStatus, member})

  const value = {
    member,
    ipRight,
    actualIpRight: prIpRight,
    renewals,
    instruct,
    ipRightImportStatus,
    renewalsStatus,
    isLoading,
  }

  return <RenewalOverviewContext.Provider {...{value}}>{children}</RenewalOverviewContext.Provider>
}
